import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators';
import store from '@app/componentlib/src/store';
import { apiUrl } from '@app/componentlib/src/application/helpers/api';
import { httpService } from '@app/componentlib/src/services/HttpService';
import {
  IJobSearchState,
  IJobPosting,
  IJobResult,
  IUserInfo,
  ISearchFilters,
  ISearchParams,
  IDateItem,
  IFilterItem,
} from '@app/componentlib/common/types/JobSearchTypes';
import Vue from 'vue';
import { AppModule } from './app';
import { LocationModule } from './locationdata';

const jobsearchApi = 'JobSearchApiV2';

@Module({
  dynamic: true,
  store,
  name: 'jobdata',
  namespaced: true,
})
class JobSearchData extends VuexModule implements IJobSearchState {
  _initialized = false;

  _jobResult: IJobResult | undefined;
  _jobPostings: IJobPosting[] = [];
  _savedSearches: ISearchParams[] = [];

  _userInfo: IUserInfo | undefined;
  _jobKeywords: string[] = [];
  _searchFilters: ISearchFilters | undefined;
  _jobsFromPartners: boolean = false;

  _activeIndex = 0;
  _selectedJobPosting: IJobPosting | undefined;

  today = new Date();
  _searchParams: ISearchParams = {
    Id: 0,
    Page: 0,
    SearchName: '',
    FilterStrings: [],
    MatchAll: false,
    TextSearch: false,
    Country: 'DK',
    ZipCode: undefined,
    Range: 25,
    StartDate: new Date(
      this.today.getFullYear(),
      this.today.getMonth(),
      this.today.getDate() - 14
    ),
    StartDateString: '',
    IndustryFilter: [],
    CareerFilter: [],
    JobCategoryFilter: [],
    paramsName: 'new',
    PartnersOnly: this.jobsFromPartners,
    CityName: '',
  };

  _tempSearchParams = this._searchParams;
  _emptyParams = this._searchParams;

  _dates: IDateItem[] = [];

  _resizeDoneDesktop: boolean = false;

  @Action({ rawError: true })
  async init() {
    console.log('JOBSEARCHDATA: Initialized');
    this.context.commit('SET_INITIALIZED', true);
  }

  @Action({ rawError: true })
  async setSearchParams(searchParams: ISearchParams) {
    searchParams.ZipCode = LocationModule.locations[0]
      ? LocationModule.locations[0].postal
      : undefined;
    searchParams.Range = LocationModule.locations[0]
      ? LocationModule.locations[0].radius
      : 10;
    this.SET_SEARCHPARAMS(searchParams);
  }

  @Action({ rawError: true })
  async setSearchParamsFromSaved(searchParams: ISearchParams) {
    this.SET_SEARCHPARAMS(this.emptyParams);
    searchParams.Page = 0;
    (searchParams.IndustryFilter =
      searchParams.IndustryFilter[0] !== '' ? searchParams.IndustryFilter : []),
      (searchParams.CareerFilter =
        searchParams!.CareerFilter[0] !== '' ? searchParams.CareerFilter : []),
      (searchParams.JobCategoryFilter =
        searchParams!.JobCategoryFilter[0] !== ''
          ? searchParams.JobCategoryFilter
          : []),
      this.SET_SEARCHPARAMS(searchParams);
  }

  @Action({ rawError: true })
  async setTempSearchParams() {
    this.SET_TEMPSEARCHPARAMS({
      Id: this.searchParams!.Id !== undefined ? this.searchParams!.Id : 0,
      Page: this.searchParams!.Page,
      SearchName: this.searchParams!.SearchName,
      FilterStrings: this.searchParams!.FilterStrings,
      MatchAll: this.searchParams!.MatchAll,
      TextSearch: this.searchParams!.TextSearch,
      Country: this.searchParams!.Country,
      ZipCode: this.searchParams!.ZipCode,
      Range: this.searchParams!.Range,
      StartDate: this.searchParams!.StartDate,
      StartDateString: this.searchParams!.StartDateString,
      IndustryFilter: this.searchParams!.IndustryFilter,
      CareerFilter: this.searchParams!.CareerFilter,
      JobCategoryFilter: this.searchParams!.JobCategoryFilter,
      paramsName: 'temp',
      PartnersOnly: this.jobsFromPartners,
      CityName: '',
    });
  }

  @Action({ rawError: true })
  async searchForJobs(bools: any) {
    const url = apiUrl(jobsearchApi, 'FilterJobs');
    let result: IJobResult | undefined;
    let useParams: ISearchParams;

    if (bools.isInfiniteScroll === true) {
      useParams = this._tempSearchParams;
      useParams.Page += 1;
      this.SET_TEMPSEARCHPARAMS(useParams);
    } else {
      useParams = this.searchParams as ISearchParams;
      useParams.ZipCode =
        LocationModule.locations[0] !== undefined
          ? LocationModule.locations[0].postal
          : undefined;
      useParams.Range =
        LocationModule.locations[0] !== undefined
          ? LocationModule.locations[0].radius
          : 50;

      // Check if search is from saved, if yes then find correct date object
      if (bools.isFromSaved === true) {
        let tempDate = this.dates.find(
          (date: IDateItem) => date.Id.toString() === useParams.StartDateString
        ) as IDateItem;
        useParams.StartDate = new Date(tempDate.Date);
      }
      useParams.Page = 0;
      useParams.Country = LocationModule.selectedCountry.countryCode;
      this.SET_TEMPSEARCHPARAMS(useParams);
    }
    await httpService
      .post(url, useParams)
      .then((data) => {
        if (data !== null && data !== undefined) {
          result = data as unknown as IJobResult;
          const tempList: IJobPosting[] = [];
          if (result !== null) {
            for (const item in result.Results) {
              result.Results[item].NumberOfAds = 0;
              result.Results[item].ListOfLinks = [];
              tempList.push(result.Results[item] as IJobPosting);
            }

            result.Results = tempList;
          } else {
            console.log('RESULT WAS NULL');
          }
          this.SET_JOBRESULT(result);
        } else {
          this.SET_JOBRESULT(undefined);
        }
      })
      .finally(() => {
        if (window.innerWidth > 767 && result !== undefined) {
          this.SET_SELECTEDJOBPOSTING(result?.Results[this.activeIndex]);
        }
      })
      .catch(() => console.log('ERROR IN SEARCH'));
  }

  @Action({ rawError: true })
  async getLastSearch(pageNumber: number) {
    const url = apiUrl(jobsearchApi, 'GetLastSearch', { page: pageNumber });
    let result: IJobResult | undefined;

    await httpService
      .get(url)
      .then((data) => {
        if (data !== null && data !== undefined) {
          result = data as unknown as IJobResult;
          const tempList: IJobPosting[] = [];
          if (result != null) {
            for (const item in result.Results) {
              result.Results[item].NumberOfAds = 0;
              result.Results[item].ListOfLinks = [];
              tempList.push(result.Results[item] as IJobPosting);
            }

            result.Results = tempList;
          } else {
            console.log('RESULT WAS NULL');
          }
          this.SET_JOBRESULT(result);
        } else {
          this.SET_JOBRESULT(undefined);
        }
      })
      .finally(() => {
        if (
          window.innerWidth > 767 &&
          result !== undefined &&
          pageNumber === 0
        ) {
          this.SET_SELECTEDJOBPOSTING(result?.Results[this.activeIndex]);
        }
      });
  }

  @Action({ rawError: true })
  async setJobPostings(jobPostings: IJobPosting[]) {
    this.SET_JOBPOSTINGS(jobPostings);
  }

  @Action({ rawError: true })
  async setJobKeywords(keywords: string[]) {
    this.SET_JOBKEYWORDS(keywords);
  }

  @Action({ rawError: true })
  async setUserInfo(userInfo: IUserInfo) {
    await this.setSearchFilters(userInfo.languageCode!);
    await this.SET_USERINFO(userInfo);
  }

  @Action({ rawError: true })
  async setSearchFilters(language: string) {
    const url = apiUrl(jobsearchApi, 'GetJobFilters', { language: language });
    await httpService.get(url).then((data) => {
      this.SET_SEARCHFILTERS(data);
    });
  }

  @Action({ rawError: true })
  async setJobsFromPartners(jobsFromPartners: boolean) {
    this.SET_JOBSFROMPARTNERS(jobsFromPartners);
  }

  @Action({ rawError: true })
  async getSavedSearches() {
    const url = apiUrl(jobsearchApi, 'GetSavedSearches');
    await httpService.get(url).then((data) => {
      console.log('SAVED DATA', data);
      let tempList: ISearchParams[] = [];
      for (let item in data) {
        tempList.push({
          Id: data[item].Id,
          Page: data[item].Page,
          SearchName: data[item].SearchName,
          FilterStrings:
            data[item].PrimaryFilter !== ''
              ? data[item].PrimaryFilter.split(',')
              : [],
          MatchAll: data[item].MatchAll,
          TextSearch: data[item].TextSearch,
          Country: data[item].Country,
          ZipCode: data[item].ZipCode,
          Range: data[item].Range,
          StartDate: undefined,
          StartDateString: data[item].FromDate,
          IndustryFilter: data[item].IndustryFilter.split(','),
          CareerFilter: data[item].CareerFilter.split(','),
          JobCategoryFilter: data[item].JobCategoryFilter.split(','),
          paramsName: 'new',
          PartnersOnly: this.jobsFromPartners,
          CityName: data[item].CityName === null ? '' : data[item].CityName,
        });
      }
      this.SET_SAVEDSEARCHES(tempList);
    });
  }

  @Action({ rawError: true })
  async saveSearch(searchName: string) {
    if (searchName !== undefined) {
      this._tempSearchParams.SearchName = searchName;
      const url = apiUrl(jobsearchApi, 'SaveSearch');
      let tempDate = this.dates.find(
        (date: IDateItem) =>
          date.Date.toString() ===
            this._tempSearchParams.StartDate?.toString() ||
          date.Id.toString() === this._tempSearchParams.StartDateString
      ) as IDateItem;
      this._tempSearchParams.StartDateString = tempDate.Id.toString();
      this._tempSearchParams.CityName = LocationModule.locations[0]
        ? LocationModule.locations[0].city
        : '';
      await httpService.post(url, this._tempSearchParams).then((data) => {
        return data;
      });
    }
  }

  @Action({ rawError: true })
  async deleteSavedSearch(id: number) {
    const url = apiUrl(jobsearchApi, 'DeleteSearch');
    await httpService.delete(url, id).then((data) => {
      return data;
    });
  }

  @Action({ rawError: true })
  async setDates(dates: IDateItem[]) {
    this.SET_DATES(dates);
  }

  @Action({ rawError: true })
  async clearSearch() {
    this.setSearchParams(this.emptyParams);
  }

  @Action({ rawError: true })
  async setSearchFiltersFromObject(filters: any) {
    let tempParams = this.searchParams;
    if (this.searchFilters !== undefined) {
      const { CareerFilters, IndustryFilters, JobCategoryFilters } =
        this.searchFilters;

      tempParams.CareerFilter = await this.displayFilterName({
        listOfFilters: filters.CareerFilter,
        listToIterate: CareerFilters,
      });
      tempParams.IndustryFilter = await this.displayFilterName({
        listOfFilters: filters.IndustryFilter,
        listToIterate: IndustryFilters,
      });
      tempParams.JobCategoryFilter = await this.displayFilterName({
        listOfFilters: filters.JobCategoryFilter,
        listToIterate: JobCategoryFilters,
      });
      tempParams.StartDateString = filters.DateFilter;

      var id = 3

      if(filters.DateFilter !== undefined) {
        if(filters.DateFilter == 'LastMonth') {
          id = 4
        } else if(filters.DateFilter == 'LastTwoWeeks') {
          id = 3
        } else if(filters.DateFilter == 'LastWeek') {
          id = 2
        } else if(filters.DateFilter == 'Last24Hours') {
          id = 1
        }
      }

      let tempDate = this.dates.find(
        (date: IDateItem) =>
          date.Id === id
      ) as IDateItem;
      if (tempDate !== undefined)
        tempParams.StartDate = new Date(tempDate.Date);
      this.setSearchParams(tempParams);
    }
  }

  @Action({ rawError: true })
  // Saved search only have the filters id, search for its name to display it
  displayFilterName(params: any) {
    let tempString = [];
    for (let item in params.listToIterate) {
      for (let item2 in params.listOfFilters) {
        if (
          params.listToIterate[item].Id.toString() ===
          params.listOfFilters[item2].toString()
        ) {
          tempString.push(params.listToIterate[item].Id);
        }
      }
    }
    return tempString;
  }

  @Action({ rawError: true })
  async setActiveIndex(index: number) {
    this.SET_ACTIVEINDEX(index);
  }

  @Action({ rawError: true })
  setSelectedJobPosting(jobPosting: IJobPosting | undefined) {
    this.SET_SELECTEDJOBPOSTING(jobPosting);
  }

  @Action({ rawError: true })
  async setResizeDoneDesktop(resizeDone: boolean) {
    this.SET_RESIZEDONEDESKTOP(resizeDone);
  }

  @Mutation
  SET_INITIALIZED(val: boolean) {
    this._initialized = val;
  }

  @Mutation
  SET_SEARCHPARAMS(searchParams: ISearchParams) {
    Vue.set(this, '_searchParams', searchParams);
  }

  @Mutation
  SET_TEMPSEARCHPARAMS(searchParams: ISearchParams) {
    Vue.set(this, '_tempSearchParams', searchParams);
  }

  @Mutation
  SET_JOBRESULT(jobResult?: IJobResult) {
    Vue.set(this, '_jobResult', jobResult);
  }

  @Mutation
  SET_JOBPOSTINGS(jobPostings: IJobPosting[]) {
    Vue.set(this, '_jobPostings', jobPostings);
  }

  @Mutation
  SET_JOBKEYWORDS(jobKeywords: string[]) {
    Vue.set(this, '_jobKeywords', jobKeywords);
  }

  @Mutation
  SET_USERINFO(userInfo: IUserInfo) {
    Vue.set(this, '_userInfo', userInfo);
  }

  @Mutation
  SET_SEARCHFILTERS(searchFilters: ISearchFilters) {
    Vue.set(this, '_searchFilters', searchFilters);
  }

  @Mutation
  SET_JOBSFROMPARTNERS(jobsFromPartners: boolean) {
    Vue.set(this, '_jobsFromPartners', jobsFromPartners);
  }

  @Mutation
  SET_SAVEDSEARCHES(savedSearces: ISearchParams[]) {
    Vue.set(this, '_savedSearches', savedSearces);
  }

  @Mutation
  SET_DATES(dates: IDateItem[]) {
    Vue.set(this, '_dates', dates);
  }

  @Mutation
  SET_ACTIVEINDEX(index: number) {
    Vue.set(this, '_activeIndex', index);
  }

  @Mutation
  SET_SELECTEDJOBPOSTING(jobPosting: IJobPosting | undefined) {
    Vue.set(this, '_selectedJobPosting', jobPosting);
  }

  @Mutation
  SET_RESIZEDONEDESKTOP(resizeDone: boolean) {
    Vue.set(this, '_resizeDoneDesktop', resizeDone);
  }

  // Getters
  get initialized(): boolean {
    return this._initialized;
  }

  get t(): any {
    return AppModule.translations;
  }

  get searchParams(): ISearchParams {
    return this._searchParams;
  }

  get tempSearchParams(): ISearchParams | undefined {
    return this._tempSearchParams;
  }

  get jobResult(): IJobResult | undefined {
    return this._jobResult;
  }

  get jobPostings(): IJobPosting[] | undefined {
    return this._jobPostings;
  }

  get jobKeywords(): string[] {
    return this._jobKeywords;
  }

  get userInfo(): IUserInfo | undefined {
    return this._userInfo;
  }

  get searchFilters(): ISearchFilters | undefined {
    return this._searchFilters;
  }

  get jobsFromPartners(): boolean {
    return this._jobsFromPartners;
  }

  get savedSearches(): ISearchParams[] {
    return this._savedSearches;
  }

  get dates(): IDateItem[] {
    return this._dates;
  }

  get emptyParams(): ISearchParams {
    return this._emptyParams;
  }

  get activeIndex(): number {
    return this._activeIndex;
  }

  get selectedJobPosting(): IJobPosting | undefined {
    return this._selectedJobPosting;
  }

  get resizeDoneDesktop(): boolean {
    return this._resizeDoneDesktop;
  }
}

export const JobSearchModule = getModule(JobSearchData);
